import { components, OptionProps, SingleValue } from 'react-select';
import { useEffect, useMemo, useState } from 'react';
import { JxtLocationTypeEnum, LocationCompressed, LocationService } from '@jooxter/api';
import { JxtAsyncSelect, SelectOption } from '@jooxter/ui';
import { IJxtBuildingSelect } from './types';
import { TextHighlight } from '@jooxter/utils';
import { useTranslation } from 'react-i18next';

const Option = (props: OptionProps<{ value: number; label: string }>) => {
  const userInput = props?.selectProps?.inputValue || '';
  const label = props?.data?.label || '';
  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        {userInput?.length ? (
          label?.split(' ').length ? (
            <span>
              <TextHighlight text={label} searchTerm={userInput} />
            </span>
          ) : (
            label
          )
        ) : (
          label
        )}
      </div>
    </components.Option>
  );
};

export const JxtBuildingSelect = ({
  onBuildingSelect,
  defaultValue,
  label,
  value,
  helperText,
  disabled,
  searchOptions,
  autoSelectFirstOption = false,
}: IJxtBuildingSelect) => {
  const { t } = useTranslation();
  const [buildings, setBuildings] = useState<LocationCompressed[]>([]);

  const formatOption = (building: LocationCompressed) => ({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    value: building.id!,
    label: building.name,
  });

  const defaultOption = useMemo(
    () => (autoSelectFirstOption && buildings.length > 0 ? formatOption(buildings[0]) : defaultValue),
    [defaultValue, buildings, autoSelectFirstOption]
  );

  useEffect(() => {
    if (autoSelectFirstOption && !value) {
      handleBuildingSelection(defaultOption as SingleValue<SelectOption<number>>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOption]);

  const handleBuildingSelection = (option: SingleValue<SelectOption<number>>) => {
    if (option) {
      const buildingId = option.value;
      const selectedBuilding = buildings?.find((building) => building.id === buildingId);
      if (selectedBuilding && onBuildingSelect) {
        onBuildingSelect(selectedBuilding);
      }
    }
  };

  async function loadOptions(search: string, loadedOptions: unknown, additional: { page?: string } | undefined) {
    const response = await LocationService.getLocationsWithPagination({
      ...searchOptions,
      name: search,
      type: JxtLocationTypeEnum.BUILDING,
      size: 25,
      page: additional?.page,
    }).then(async (res) => {
      setBuildings([...buildings, ...res.data]);

      return {
        data: res.data,
        nextPage: res.nextPage,
      };
    });

    return {
      options: response.data.map((b) => formatOption(b)),
      hasMore: !!response.nextPage,
      additional: {
        page: response.nextPage,
      },
    };
  }

  return (
    <JxtAsyncSelect
      key={`${defaultOption?.value}-${JSON.stringify(searchOptions)}`}
      placeholder={t<string>('choose-a-building')}
      defaultValue={defaultOption}
      value={value}
      label={label}
      helperText={helperText}
      loadOptions={loadOptions}
      handleSelection={handleBuildingSelection}
      components={{ Option }}
      isDisabled={disabled}
      noOptionsMessage={t<string>('no-matching-buildings')}
    />
  );
};
